import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLogin } from "../../hooks/useApi";
import { isAuthenticated } from "../../lib/api";

export default function Login() {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showUnverifiedModal, setShowUnverifiedModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Auth Guard: redirect immediately to /profile if already logged in
  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/profile");
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    loginMutation.mutate(
      { uusrEmail: email, uusrPassword: password },
      {
        onSuccess: () => {
          navigate("/profile");
        },
        onError: (err: any) => {
          const errMsg = err.message || "Failed to log in";
          if (errMsg.toLowerCase().includes("email not verified")) {
            setShowUnverifiedModal(true);
          } else {
            setErrorMessage(errMsg);
          }
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-brand-blue flex items-center justify-center px-4 py-36 md:py-48 relative overflow-hidden font-poppins">
      {/* Background decoration elements */}
      <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-brand-orange/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-2xl shadow-2xl transition-all duration-300 hover:border-white/30">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-evil text-white tracking-wider mb-2">SIGN IN</h2>
        </div>

        {errorMessage && (
          <div className="mb-6 bg-red-500/20 border border-red-500/50 text-red-100 px-4 py-3 rounded-lg text-sm text-center">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-xs font-semibold text-white uppercase tracking-wider mb-2" htmlFor="email">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl bg-white text-black border border-white/20 focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all duration-200"
              placeholder="e.g. runner@tangerang10k.com"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-white uppercase tracking-wider mb-2" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl bg-white text-black border border-white/20 focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all duration-200"
              placeholder="••••••••"
            />
            <p className="text-white/60 text-sm text-right mt-2">
              <a
                href="https://ultimate10kseries.com/forgot-password"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-orange hover:underline font-semibold"
              >
                Forgot password?
              </a>
            </p>
          </div>

          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full py-4 bg-brand-orange text-white font-evil tracking-wider text-xl rounded-xl hover:bg-brand-orange/90 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-brand-orange/20"
          >
            {loginMutation.isPending ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                SIGNING IN...
              </>
            ) : (
              "SIGN IN"
            )}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-white/10 text-center">
          <p className="text-white/60 text-sm">
            Don't have a runner account?{" "}
            <a
              href="https://ultimate10kseries.com/daftar"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-orange hover:underline font-semibold"
            >
              Register here
            </a>
          </p>
        </div>
      </div>

      {/* UnverifiedModal */}
      {showUnverifiedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="relative w-full max-w-md bg-[#163a6e] border border-white/20 p-8 rounded-2xl shadow-2xl text-center text-white">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-500/20 text-yellow-400 mb-6">
              <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h3 className="text-2xl font-evil tracking-wider mb-3">EMAIL NOT VERIFIED</h3>

            <p className="text-white/80 text-sm leading-relaxed mb-6">
              Your email verification is currently pending. Please check your inbox (and spam folder) for the verification link sent to you, then try logging in again.
            </p>

            <button
              onClick={() => setShowUnverifiedModal(false)}
              className="w-full py-3 bg-white text-brand-blue font-semibold rounded-xl hover:bg-white/90 active:scale-[0.98] transition-all duration-200 shadow-md"
            >
              UNDERSTOOD
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
